import { FlightResponse, GetFlightGroupResponse } from '@orbit/api-types'
import { formatUnix, formatFlightWallClock } from '@lib/datetime'

/**
 * "AC 884" from airline + number. Structurally typed so callers that only carry
 * the two code fields (e.g. the trip-overview normalizer) can reuse it.
 */
export const getFlightCode = (leg: { airlineIata: string; flightNumber?: string | null }): string => {
	const parts: string[] = []
	if (leg.airlineIata) parts.push(leg.airlineIata)
	if (leg.flightNumber) parts.push(leg.flightNumber)
	return parts.join(' ') || 'Flight'
}

export const getRouteString = (leg: FlightResponse): string => {
	const dep = leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? '—'
	const arr = leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? '—'
	return `${dep} → ${arr}`
}

// Flight times always render on the 24-hour clock (aviation convention,
// issue #164), on both tiers — real instants and manual wall-clock strings.
const formatUnixDateTime = (unix: number | null | undefined): string | null =>
	formatUnix(unix, 'datetime24')

export const getDepartureDisplay = (leg: FlightResponse): string | null => {
	if (leg.departureTime != null) {
		return formatUnixDateTime(leg.departureTime)
	}
	return formatFlightWallClock(leg.departureTimeStr)
}

export const getArrivalDisplay = (leg: FlightResponse): string | null => {
	if (leg.arrivalTime != null) {
		return formatUnixDateTime(leg.arrivalTime)
	}
	return formatFlightWallClock(leg.arrivalTimeStr)
}

export const getDurationString = (minutes: number | null | undefined): string | null => {
	if (minutes == null) return null
	const h = Math.floor(minutes / 60)
	const m = minutes % 60
	return h > 0 ? `${h}h ${m}m` : `${m}m`
}

/**
 * "Layover · 1h 25m" between one leg's arrival and the next leg's departure
 * (Unix seconds). Null when either instant is missing or the gap is not
 * positive — manual legs carry wall-clock strings only, and those never
 * produce a layover banner.
 */
export const getLayoverString = (
	prevArrival: number | null | undefined,
	nextDeparture: number | null | undefined,
): string | null => {
	if (prevArrival == null || nextDeparture == null) return null
	const minutes = Math.floor((nextDeparture - prevArrival) / 60)
	if (minutes <= 0) return null
	return `Layover · ${getDurationString(minutes)}`
}

export const getGroupEarliestDeparture = (group: GetFlightGroupResponse): number | null => {
	let earliest: number | null = null
	for (const leg of group.flights) {
		if (leg.departureTime != null) {
			if (earliest == null || leg.departureTime < earliest) {
				earliest = leg.departureTime
			}
		}
	}
	return earliest
}

/**
 * A leg's departure as a lexicographically comparable "YYYY-MM-DD HH:MM:SS" key.
 * Real instants render through UTC; manual flights fall back to their wall-clock
 * `departureTimeStr` as-is (no zone to shift — the ordering is a best-effort
 * heuristic across the two tiers, which share the same string shape).
 */
const legDepartureKey = (leg: FlightResponse): string | null => {
	if (leg.departureTime != null) {
		return new Date(leg.departureTime * 1000).toISOString().slice(0, 19).replace('T', ' ')
	}
	return leg.departureTimeStr || null
}

const groupEarliestDepartureKey = (group: GetFlightGroupResponse): string | null => {
	let earliest: string | null = null
	for (const leg of group.flights) {
		const key = legDepartureKey(leg)
		if (key != null && (earliest == null || key < earliest)) {
			earliest = key
		}
	}
	return earliest
}

/**
 * Order flight groups chronologically by their earliest departure. Manual
 * flights (no real instant) participate via their wall-clock string instead of
 * sorting last; groups with no departure at all go to the end.
 */
export const sortGroupsByDeparture = (groups: GetFlightGroupResponse[]): GetFlightGroupResponse[] => {
	// '￿' sorts after any date string → undated groups last.
	const UNDATED = '￿'
	return [...groups].sort((a, b) => {
		const aKey = groupEarliestDepartureKey(a) ?? UNDATED
		const bKey = groupEarliestDepartureKey(b) ?? UNDATED
		return aKey < bKey ? -1 : aKey > bKey ? 1 : 0
	})
}

/** Splits "AC123" into ("AC", "123"). */
export const splitFlightCode = (code: string): { airlineIata: string; flightNumber: string } => {
	const trimmed = code.trim().replace(/\s/g, '').toUpperCase()
	const digitIndex = trimmed.search(/\d/)
	if (digitIndex === -1) {
		return { airlineIata: trimmed, flightNumber: '' }
	}
	return {
		airlineIata: trimmed.slice(0, digitIndex),
		flightNumber: trimmed.slice(digitIndex),
	}
}

